import { useCallback, useMemo, useState } from "react";
import { Button, Flex, SegmentedControl, Text, TextInput } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { VALID_VIEW_FILTERS, type ViewFilter } from "../types";
import { QueriesGrid, type Query } from "./QueriesGrid";
import * as styles from "./styles_vanilla.css";

const isViewFilter = (value: string): value is ViewFilter =>
  (VALID_VIEW_FILTERS as readonly string[]).includes(value);

type QueriesPanelProps = {
  queries: Query[];
  viewFilter: ViewFilter;
  onViewFilterChange: (filter: ViewFilter) => void;
  onEditQuery?: (queryId: string) => void;
  onNewQuery?: () => void;
  isDataPackageArchived?: boolean;
};

export const QueriesPanel = ({
  queries,
  viewFilter,
  onViewFilterChange,
  onEditQuery,
  onNewQuery,
  isDataPackageArchived = false,
}: QueriesPanelProps) => {
  const [searchValue, setSearchValue] = useState("");

  const filteredQueries = useMemo(() => {
    if (isDataPackageArchived) return queries;
    return queries.filter((q) =>
      viewFilter === "active" ? q.isActive !== false : q.isActive === false,
    );
  }, [queries, viewFilter, isDataPackageArchived]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleViewFilterChange = useCallback(
    (value: string) => {
      if (isViewFilter(value)) onViewFilterChange(value);
    },
    [onViewFilterChange],
  );

  return (
    <Flex flexDirection="column" className={styles.queriesPanel}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap={SPACING.MEDIUM}
        className={styles.queriesPanelHeader}
      >
        <Text variant="upper-md" className={styles.queryPanelTitle}>
          Queries
        </Text>
        <Flex alignItems="center" gap={SPACING.SMALL}>
          <TextInput
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search queries"
            icon="search"
            isClearable
            onClearValue={handleClearSearch}
            data-testid="queries-search"
          />
          {!isDataPackageArchived && (
            <SegmentedControl
              value={viewFilter}
              onChange={handleViewFilterChange}
              options={[
                { label: "Active", value: "active" },
                { label: "Archived", value: "archived" },
              ]}
              data-testid="queries-view-filter"
            />
          )}
          {onNewQuery && !isDataPackageArchived && (
            <Button
              variant="primary"
              icon="plus"
              onClick={onNewQuery}
              data-testid="new-query-btn"
            >
              New query
            </Button>
          )}
        </Flex>
      </Flex>
      <Flex flexGrow={1} className={styles.queriesTableContainer}>
        <QueriesGrid
          queries={filteredQueries}
          searchValue={searchValue}
          viewFilter={isDataPackageArchived ? "archived" : viewFilter}
          onEditQuery={onEditQuery}
        />
      </Flex>
    </Flex>
  );
};
